import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api';
import SiteCard from '../components/SiteCard';
import DeployModal from '../components/DeployModal';
import ComposeModal from '../components/ComposeModal';
import './Dashboard.css';

export default function Dashboard({ onLogout }) {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDeploy, setShowDeploy] = useState(false);
  const [editingSite, setEditingSite] = useState(null);
  const [actionStatus, setActionStatus] = useState({});

  const loadSites = useCallback(async () => {
    try {
      const data = await api.getSites();
      setSites(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSites();
    const interval = setInterval(loadSites, 15000);
    return () => clearInterval(interval);
  }, [loadSites]);

  async function handleAction(domain, action) {
    setActionStatus(s => ({ ...s, [domain]: action }));
    try {
      await api.siteAction(domain, action);
      await loadSites();
    } catch (e) {
      alert(`Error: ${e.message}`);
    } finally {
      setActionStatus(s => ({ ...s, [domain]: null }));
    }
  }

  async function handleDelete(domain) {
    if (!window.confirm(`Delete ${domain}? This will stop containers and remove the directory.`)) return;
    try {
      await api.deleteSite(domain);
      await loadSites();
    } catch (e) {
      alert(`Error: ${e.message}`);
    }
  }

  const runningCount = sites.reduce((n, s) => n + s.containers.filter(c => c.running).length, 0);
  const totalContainers = sites.reduce((n, s) => n + s.containers.length, 0);

  return (
    <div className="dashboard">
      <header className="dash-header">
        <div className="dash-logo">
          <span className="dash-icon">⬡</span>
          <div>
            <h1>SERVER ADMIN</h1>
            <span className="dash-sub">leon@mail · /home/leon/docker/clients</span>
          </div>
        </div>
        <div className="dash-stats">
          <div className="stat">
            <span className="stat-val">{sites.length}</span>
            <span className="stat-label">sites</span>
          </div>
          <div className="stat">
            <span className="stat-val" style={{ color: 'var(--accent)' }}>{runningCount}</span>
            <span className="stat-label">running</span>
          </div>
          <div className="stat">
            <span className="stat-val">{totalContainers}</span>
            <span className="stat-label">containers</span>
          </div>
        </div>
        <div className="dash-actions">
          <button className="btn-primary" onClick={() => setShowDeploy(true)}>
            + Deploy Site
          </button>
          <button className="btn-ghost" onClick={onLogout}>
            Logout
          </button>
        </div>
      </header>

      <main className="dash-main">
        {error && (
          <div className="dash-error">⚠ {error}</div>
        )}

        {loading ? (
          <div className="dash-loading">
            <div className="spinner" />
            <span>Loading sites...</span>
          </div>
        ) : sites.length === 0 ? (
          <div className="dash-empty">
            <p>No sites deployed yet.</p>
            <button className="btn-primary" onClick={() => setShowDeploy(true)}>Deploy your first site →</button>
          </div>
        ) : (
          <div className="sites-grid">
            {sites.map(site => (
              <SiteCard
                key={site.domain}
                site={site}
                actionStatus={actionStatus[site.domain]}
                onAction={handleAction}
                onDelete={handleDelete}
                onEdit={setEditingSite}
              />
            ))}
          </div>
        )}
      </main>

      {showDeploy && (
        <DeployModal
          onClose={() => setShowDeploy(false)}
          onDeployed={() => { setShowDeploy(false); loadSites(); }}
        />
      )}

      {editingSite && (
        <ComposeModal
          domain={editingSite}
          onClose={() => setEditingSite(null)}
          onSaved={() => { setEditingSite(null); loadSites(); }}
        />
      )}
    </div>
  );
}
